import React, { useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment-timezone";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import axios from "axios";
import {Grid, InputLabel} from "@material-ui/core";

import ChromeTabs from "../../../../controls/ChromeTabs";
import {FormCheckbox, FormDialog, MaskedNumber, Text} from "../../../../controls";
import {getApiSitePrefix} from "../../../../functions/SiteDefaults";

import Validator from "../../validators/CateringEditorValidator";
import CateringEditor from "./CateringEditor";
import { fetch as fetchComboValues } from "../../../../services/settings/useComboValues";
import CompanyUuidLookup from "../../../Companies/components/CompanyUuidLookup";
import TripLegMultiSelect from '../../../../controls/TripLegMultiSelect';

const apiSitePrefix = getApiSitePrefix();

const EditCateringDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const SystemData = props.systemData;
  const extraKeys = props.extraKeys;
  const handleUpdate = props.handleUpdateFlight
  const classes = props.classes;

  const eventBus = SystemData.eventBus;

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    title: 'Edit Catering Order',
    message: "",
    skeletonMeal: null,
    skeletonDryStore: null,
    extraKeys: extraKeys,
    tabs: [],
    currentTab: "",
    blockTime: "00:00:00",
    departureTimeLocal: "00:00:00",
    crewNumber: 0,
    additionalSectors: [],
  });
  const [defaultValues, setDefaultValues] = useState({
    defaultTemplate: null,
    defaultAttachment: null,
  });

  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/cateringorders/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Catering Order Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Catering Order Template. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Catering Order Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Catering Order Template. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Catering Order Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Catering Order Template. Server is not responding`);
    }
  }

  useEffect(()=> {
    const skeletonExtraKeys = ["CATERING", "MEAL"];
    const skeletonDryExtraKeys = ["CATERING", "DRY STORES"];


    const getLatestOrder = async (tripLegUuid) => {
      try {
        const cateringOrder = await axios.get(`${apiSitePrefix}/cateringorders/getLatestOrder/${tripLegUuid}`);
        return cateringOrder;
      } catch(err) {
        if (__.has(err, "response") && __.has(err.response, "data")) {
          if (err.response.data.error) {
            SystemData.openAlert("Failed to retrieve Catering Order", "An Unexpected error has occurred: " + err.response.data.errorMessage);
          }
        } else {
          SystemData.openAlert("Failed to retrieve Catering Order", "An Unexpected error has occurred!");
        }
      }
    }

    const getLegCount = async (tripUuid) => {
      try {
        const sectorCount = await axios.get(`${apiSitePrefix}/trip/getsectorcount/${tripUuid}`);
        if (sectorCount.data && sectorCount.data.success) {
          return sectorCount.data.legCount;
        }
        SystemData.openAlert("Failed to get trip sector count", sectorCount?.data?.error);
        return 1;
      } catch(err) {
        if (__.has(err, "response") && __.has(err.response, "data")) {
          if (err.response.data.error) {
            SystemData.openAlert("Failed to get trip sector count", "An Unexpected error has occurred: " + err.response.data.errorMessage);
          }
        } else {
          SystemData.openAlert("Failed to get trip sector count", "An Unexpected error has occurred!");
        }
        return 1
      }
    }

    const open = async (record, updateKeys) => {
      const skeletonMealData = await fetchComboValues({queryClient, extraKeys: skeletonExtraKeys, useCache: true});
      const skeletonCatering = {data:skeletonMealData};
      const skeletonDryStoreData = await fetchComboValues({queryClient, extraKeys: skeletonDryExtraKeys, useCache: true});
      const skeletonDryStore = {data:skeletonDryStoreData};

      let currentVersion = "";
      const latestOrder = await getLatestOrder(record.tripLegUuid);
      if (latestOrder?.data && Array.isArray(latestOrder?.data) && latestOrder?.data?.length > 0) {
        const flightCateringOrder = {
          ...latestOrder.data[0],
        }
        record["flightCateringOrder"] = flightCateringOrder;
        if (flightCateringOrder) {
          record["supplierUuid"] = flightCateringOrder.supplierUuid ;
          record["additionalSectors"] = flightCateringOrder.additionalSectors;
          record["status"] = flightCateringOrder.status;
          currentVersion = flightCateringOrder.orderVersion ? ` (current order version: ${flightCateringOrder.orderVersion})` : ``;
        }
      }
      const departureTimeLocal = moment.utc(record.scheduledDepartureTimeUTC).tz(record.departureAirportTimeZone).format("HH:mm");
      const crewNumber = record.crew && Array.isArray(record.crew) ? record.crew.length : 0;
      const sectorCount = await getLegCount(record.tripUuid);
      record["sectorCount"] = sectorCount;

      let lastArrivalTimeUTC = moment.utc(record.scheduledArrivalTimeUTC);
      if (Array.isArray(record.additionalSectors)) {
        for (let i = 0; i < record.additionalSectors.length; i++) {
          const scheduledArrivalTimeUTC = record.additionalSectors[i].scheduledArrivalTimeUTC;
          if (moment.utc(scheduledArrivalTimeUTC).isAfter(lastArrivalTimeUTC)) {
            lastArrivalTimeUTC = moment.utc(scheduledArrivalTimeUTC);
          }
        }
      }
      lastArrivalTimeUTC = lastArrivalTimeUTC.add(75, "minutes");
      const blockTime =  moment.utc(lastArrivalTimeUTC.diff(moment.utc(record.scheduledDepartureTimeUTC))).format("HH:mm");
      record["crewNumber"] = crewNumber;

      const tabs =
        [
          {label: "Meals", value: "meal"},
        ];
      if (record.status !== "SECTOR") tabs.push({label: "Dry Store", value: "drystore"});

      const currentTab = "meal";
      const title = record.status === "SECTOR" ? "View Catering Order" : `Edit Catering Order at ${record.originAirportCode} airport${currentVersion}`;
      setDialogStates(
        {
          ...dialogStates,
          title,
          message: `${record.status === "SECTOR" ? "This sector's catering is ordered at the previous airport" : ""}`,
          showDialog: true,
          record,
          skeletonMeal: skeletonCatering,
          skeletonDryStore: skeletonDryStore,
          extraKeys: updateKeys||extraKeys,
          tabs,
          currentTab,
          blockTime,
          departureTimeLocal,
          crewNumber,
          additionalSectors: record.additionalSectors
        }
      );
    }

    eventBus.bind("EditCateringDialog.open", open);

    return () => {
      eventBus.unbind("EditCateringDialog.open", open);
    }
  }, [SystemData, dialogStates, eventBus, extraKeys, queryClient]);



  const handleTabChange = (event, tab) => {
    setDialogStates({...dialogStates, currentTab: tab});
  }

  const handleAddFunction = async (record) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues();
    }

    const cateringOrder = {
      orderDate: moment.utc().format("YYYY-MM-DD"),
      airportCode: record.originAirportCode,
      tripNo: record.tripNo,
      tripLegUuid: record.tripLegUuid,
      airlineCode: record.airlineCode,
      flightNo: record.flightNo,
      flightDate: record.flightDate,
      departureTime: moment(record.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm"),
      aircraftTailNumber: record.aircraftId || "TF-AMP or sub (TF-AMC/AMN/AMI)",
      crewNumber: record.crewNumber,
      replenishDryStore: record.dryStore ?? false,
      meals: record.flightCateringOrder["meals"] || [],
      dryStores: record.dryStore ? record.flightCateringOrder["dryStores"] || [] : [],
      additionalSectors: record.flightCateringOrder["additionalSectors"] || [],
      status: "REQUIRED",
      cateringOrderUuid: uuidv4(),
      supplierUuid: record.supplierUuid,
      cateringOrderTemplateUuid: record.orderEmailTemplateUuid || defaultValues.defaultAttachment || defaults.defaultAttachment,
      force: record.force
    }

    try {
      SystemData.toggleLoadingProgress(true);
      const applied = await axios.put(`${apiSitePrefix}/flights/catering`, cateringOrder);
      if (applied && applied.error) {
        SystemData.openAlert("Generate Catering Order Failed", applied.errorMessage);
      }
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert("Order Generated", "Catering Order successfully generated. Review and Send the order in 'Catering Orders' repository");
    } catch(err) {
      SystemData.toggleLoadingProgress(false);
      if (__.has(err, "response") && __.has(err.response, "data")) {
        if (err.response.data.error) {
          SystemData.openAlert("Generate Catering Order Failed", "An Unexpected error has occurred: " + err.response.data.errorMessage);
        }
      } else {
        SystemData.openAlert("Generate Catering Order Failed", "An Unexpected error has occurred!");
      }
    }
    queryClient.invalidateQueries(['CateringOrders']);
  }

  const handleCateringChanged = (changes, props) => {

    const {record, recordChange} = props;
    if (changes && changes.record) {

      const flightCateringOrder =  changes.record.flightCateringOrder;
      record['flightCateringOrder'] = flightCateringOrder;
      recordChange({...record, flightCateringOrder});

      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  if (dialogStates.showDialog) {
    return (
      <FormDialog
        title={dialogStates.title}
        message={dialogStates.message}
        open={dialogStates.showDialog}
        record={__.cloneDeep(dialogStates.record)}
        dialogOptions={{width: "md", maxWidth: "md", height: 700}}
        classes={classes}
        validator={Validator}
        buttons = {{
          // addItem: {icon: "add_circle", key: "Add Item", label: "Add Item", color: "primary"},
          generateOrder: {icon: "add_circle", key: "Generate Order", label: "Generate Order", color: "primary", disabled: (status)=>{return status.error || dialogStates.record.status === "SECTOR"}},
          // save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{
          //   if (status.buttonState && status.buttonState.disabled) return true;
          //   return status.error;
          // }},
          cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
        }}
        onChange={(state) => {
          if (state.buttonPressed === "save") {
            const record = state.record;
            handleUpdate.mutate({
              extraKeys: dialogStates.extraKeys,
              record,
              postSuccess: () => {
                setDialogStates({...dialogStates, showDialog: false, record: null});
                if (props.afterSave) {
                  props.afterSave();
                }
                if (typeof props.onClose === 'function') {props.onClose()}
              },
              postError: (data) => {
                if (props.onError) {
                  props.onError(data);
                } else if (data.errorMessage) {
                  SystemData.alert("Error", `${data.errorMessage}`);
                } else {
                  SystemData.alert("Error", `An Unexpected Error has occurred.`);
                }
              }
            });
          } else if (state.buttonPressed === "generateOrder") {
            SystemData.confirm(
              "Generate Catering Order",
              "Are you sure you wish to generate a new version of the Catering Order?",
              [
                {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
                {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
              ],
              async (response) => {
                if (response) {
                  handleAddFunction(state.record);
                  setDialogStates({...dialogStates, showDialog: false, record: null});
                  if (typeof props.onClose === 'function') {props.onClose()}
                }
              }
            );
          } else {
            setDialogStates({...dialogStates, showDialog: false, record: null});
            if (typeof props.onClose === 'function') {props.onClose()}
          }
        }}

      >
        <Grid container checkChildren={true} spacing={3}>
          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div>
                <InputLabel shrink>
                  Flight Number
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={`${record.airlineCode}-${record.flightNo}`}
                />
              </div>
            )}
          </Grid>

          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div>
                <InputLabel shrink>
                  Flight Date
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={`${moment(record.flightDate, "YYYY-MM-DD HH:mm:ss").format(dateFormat)}`}
                />
              </div>
            )}
          </Grid>

          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div>
                <InputLabel shrink>
                  Trip Number
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={`${record.tripPrefix}${record.tripNo}`}
                />
              </div>
            )}
          </Grid>

          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div className="d-flex flex-row justify-content-between align-items-center">
                <InputLabel shrink>
                  Route
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={`${record.originAirportCode} -> ${record.destinationAirportCode}`}
                />
              </div>
            )}
          </Grid>

          <Grid item xs={3} checkChildren={true}>
            {(record, changeRecord) => (
              <div className="d-flex flex-row justify-content-between align-items-center">
                <InputLabel shrink>
                  Departure Time
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={`${moment.utc(record.scheduledDepartureTimeUTC).format("HH:mm")}Z (${dialogStates.departureTimeLocal} Local)`}
                />
              </div>
            )}
          </Grid>
          <Grid item xs={1} checkChildren={true}>
            {(record, changeRecord) => (
              <div className="d-flex flex-row justify-content-between align-items-center">
                <InputLabel shrink>
                  Duty
                </InputLabel>

                <Text
                  style={{width: "100%", marginTop: 2, display: "inline-block"}}
                  defaultValue={record.blockTime || dialogStates.blockTime}
                  id="blockTime"
                />
              </div>
            )}
          </Grid>
        </Grid>

        <Grid container checkChildren={true} spacing={3}>
          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <MaskedNumber
                id="crewNumber"
                label="Number of Crew"
                readOnly={!props.canEdit || record.status === 'SECTOR'}
                inputLabelProps={{shrink: true}}
                fullWidth
                format="##"
                onChange={(value, props) => {
                  props.recordChange({...record, crewNumber: value});
                  setDialogStates({...dialogStates, crewNumber: value});
                }}
              />
            )}
          </Grid>
          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => {
              if (record.status !== 'SECTOR') {
              return (
                <FormCheckbox
                  id="dryStore"
                  label="Replenish Dry Store"
                  key="dryStore"
                  defaultValue={record.departureAirportIsHub}
                  className={classes.checkbox}
                  readOnly={!props.canEdit || record.status === 'SECTOR'}
                  onChange={(value, props) => {
                    if (!record.departureAirportIsHub && value) {
                      SystemData.confirm(
                        `Dry Store Replenishment`,
                        `${record.originAirportCode} is not a hub airport. Are you sure you wish to order dry store replenishment?`,
                        [
                          {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
                          {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
                        ],
                        (response) => {
                          if (!response) {
                            return
                          }
                        }
                      );
                      props.recordChange({...record, dryStore: value});
                      setDialogStates({...dialogStates, currentTab: "drystore"});
                    } else {
                      props.recordChange({...record, dryStore: value});
                      setDialogStates({...dialogStates, currentTab: "meal"});
                    }
                  }}
                />
              )}}
            }
          </Grid>
          <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <CompanyUuidLookup
                companyType={"SUPPLIER"}
                id="supplierUuid"
                label="Catering Supplier"
                shrink
                readOnly={!props.canEdit || record.status === 'SECTOR'}
                returnType={"object"}
                includeNew={false}
                atAirport={record.status === 'SECTOR' ? null : record.originAirportCode}
                role="CATERING"
                onChange={(returnObject, props) => {
                  if (returnObject.found) {
                    const supplier = returnObject.data
                    const supplierCompany = supplier.companyCode === "NEW SUPPLIER" ? "" : supplier.companyName;
                    const data = {
                      ...props.record,
                      supplierCompany: supplierCompany,
                      supplierUuid: supplier.companyUuid,
                    }
                    props.recordChange(data);
                  } else {
                    props.onChange(returnObject.value);
                  }
                }}
              />
            )}
          </Grid>
          {/*<Grid item xs={4} checkChildren={true}>*/}
          {/*  {(record, changeRecord) => (*/}
          {/*    <div className="d-flex flex-row justify-content-between align-items-center">*/}
          {/*      <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">*/}
          {/*        Company Name*/}
          {/*      </label>*/}
          {/*      <Text*/}
          {/*        style={{width: "100%", marginTop: 2, display: "inline-block"}}*/}
          {/*        defaultValue={record.supplierCompany}*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  )}*/}
          {/*</Grid>*/}
          <Grid item xs={6} checkChildren={true}>
            {(record, changeRecord) => (
              <div className="d-flex flex-row justify-content-between align-items-center">
                <TripLegMultiSelect
                  id="additionalSectors"
                  tripUuid={record.tripUuid}
                  afterTripLegUuid={record.tripLegUuid}
                  label="Additional Sectors"
                  readOnly={!props.canEdit || record.status === 'SECTOR'}
                  onChange={(returnObject, props) => {
                    let lastArrivalTimeUTC = moment.utc(record.scheduledArrivalTimeUTC);
                    if (Array.isArray(returnObject)) {
                      for (let i = 0; i < returnObject.length; i++) {
                        const scheduledArrivalTimeUTC = returnObject[i].scheduledArrivalTimeUTC;
                        if (moment.utc(scheduledArrivalTimeUTC).isAfter(lastArrivalTimeUTC)) {
                          lastArrivalTimeUTC = moment.utc(scheduledArrivalTimeUTC);
                        }
                      }
                    }
                    lastArrivalTimeUTC = lastArrivalTimeUTC.add(75, "minutes");
                    const blockTime =  moment.utc(lastArrivalTimeUTC.diff(moment.utc(record.scheduledDepartureTimeUTC))).format("HH:mm");
                    props.recordChange({...record, additionalSectors: returnObject, blockTime});
                    setDialogStates({...dialogStates, additionalSectors: returnObject, blockTime});
                  }}
                />
              </div>
            )}
          </Grid>

          {(record, changeRecord) => (
            <Grid item xs={12} checkChildren={false} style={{paddingBottom: 8}}>
              <ChromeTabs
                id={"CateringTabs"}
                //style={{ alignSelf: "flex-end", display: "inline-block", align: "left" }}
                style={{marginTop: -10}}
                tabs={dialogStates.tabs}
                tabStyle={{
                  bgColor: "#dddddd",
                  selectedBgColor: "#c0c0c0",
                  color: "rgba(0,0,0,0,1)",
                }}
                tabProps={{disableRipple: false}}
                value={dialogStates.currentTab}
                onChange={(event, tab) => {
                  if (tab === "drystore" && !record.dryStore) {
                    SystemData.openAlert("Order Section Disabled", "The 'Dry Store' part of the order is only available at hub airports when 'Replenish Dry Store' is ticked");
                  } else {
                    handleTabChange(event, tab)}
                  }
                }
              />
            </Grid>
          )}
        </Grid>

        <Grid container checkChildren={true} spacing={1}>
          <Grid item xs={12} checkChildren={true}>
            {(record, changeRecord, changeButtonState) => (
              <CateringEditor
                flightRecord={record}
                skeletonMeals={dialogStates.skeletonMeal}
                skeletonDryStore={dialogStates.skeletonDryStore}
                dryStore={dialogStates.currentTab === "drystore"}
                blockTime={dialogStates.blockTime}
                departureTime={dialogStates.departureTimeLocal}
                crewNumber={dialogStates.crewNumber}
                additionalSectors={dialogStates.additionalSectors}
                handleUpdate={handleUpdate}
                canEdit={props.canEdit && record.status !== 'SECTOR'}
                classes={classes}
                onChange={handleCateringChanged}
                changeButtonState={changeButtonState}
              />
            )}
          </Grid>
        </Grid>
      </FormDialog>
    )
  }
  return (<></>);
}

export default EditCateringDialog;
